// ============================================================
// MicrosoftPanel.tsx
// Settings → Email & Microsoft: connect / disconnect a Microsoft account (Stage 1 of
// the Microsoft integration — the shared OAuth foundation the later
// Outlook-sync + email-send features hang off).
//
// Connect is a TOP-LEVEL redirect (OAuth), not a fetch: we ask the backend
// (authenticated) for the Microsoft /authorize URL, then navigate the whole
// window to it. ★ PKCE (the hijack fix): before leaving, this browser makes a
// secret code_verifier and keeps it in sessionStorage; only its hash goes to
// the backend. Microsoft redirects back to Settings with ?ms=pending (or
// ?ms=error) — the code itself waits on the server, never in the URL; this
// panel then finishes it — POST /complete with the verifier, as the signed-in
// user — so only the tab (and user) that started the connection can complete
// it, once.
// ============================================================
import React, { useCallback, useEffect, useState } from 'react'
import { msStatusApi, msConnectUrlApi, msCompleteApi, msDisconnectApi, MicrosoftStatus } from '../../api/casevoApi'
import { newPkcePair, takeStoredVerifier, storeVerifier } from '../../utils/microsoftPkce'
import ConfirmDialogue from './ConfirmDialogue'
import { useUserChange } from '../../utils/liveEvents'
import ActionBox, { ErrorNote } from './ActionBox'
import {
  SettingsCard, SettingsToggleRow, SettingsBadge,
  S_BTN_GHOST_SM, S_BTN_PRIMARY_SM,
} from '../settings/settingsUi'

interface MicrosoftPanelProps {
  // Reports the connection state after each load (mount, disconnect) so
  // Settings' section-nav sub-line ("Connected" / "Not connected") stays
  // live. null = unknown (status fetch failed).
  onStatusChange?: (connected: boolean | null) => void
}

// The OAuth return's ?ms=connected|error, captured when the app loads —
// before the navigation layer's place sync rewrites the address bar (it lands
// on /settings?ms=…; App.tsx opens Settings › Email for it). One-shot.
let msReturn: string | null = new URLSearchParams(window.location.search).get('ms')
export const peekMicrosoftReturn = (): string | null => msReturn

const MicrosoftPanel: React.FC<MicrosoftPanelProps> = ({ onStatusChange }) => {
  const [status, setStatus] = useState<MicrosoftStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [banner, setBanner] = useState<{ ok: boolean; text: string } | null>(null)
  // Casevo confirm dialogue for Disconnect (replaces the old native
  // window.confirm). false = closed.
  const [confirmOpen, setConfirmOpen] = useState(false)

  const load = useCallback(async () => {
    try {
      const next = await msStatusApi()
      setStatus(next)
      onStatusChange?.(next.connected)
    } catch {
      setStatus(null)
      onStatusChange?.(null)
    } finally {
      setLoading(false)
    }
  }, [onStatusChange])
  useEffect(() => { void load() }, [load])
  // (D12 live updates) Connected / disconnected in another tab.
  useUserChange(['preferences'], () => { void load() })

  // Consume the ?ms= result from the OAuth return, then strip it so a refresh
  // doesn't re-show the banner.
  useEffect(() => {
    const ms = msReturn
    msReturn = null
    if (ms === 'error') { setBanner({ ok: false, text: 'Couldn’t connect your Microsoft account. Please try again.' }); return }
    if (ms !== 'pending') return
    // Finish the connection with THIS browser's verifier (one use).
    const verifier = takeStoredVerifier()
    if (!verifier) { setBanner({ ok: false, text: 'This Microsoft sign-in wasn’t started in this browser. Please press Connect again.' }); return }
    setBusy(true)
    msCompleteApi(verifier)
      .then(() => { setBanner({ ok: true, text: 'Microsoft account connected.' }); void load() })
      .catch((e: unknown) => setBanner({ ok: false, text: e instanceof Error && e.message ? e.message : 'Couldn’t connect your Microsoft account. Please try again.' }))
      .finally(() => setBusy(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConnect = async (): Promise<void> => {
    setBusy(true)
    setError(null)
    try {
      const { verifier, challenge } = await newPkcePair()
      storeVerifier(verifier)
      const url = await msConnectUrlApi(challenge)
      window.location.href = url // top-level OAuth redirect
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start the Microsoft connection.')
      setBusy(false)
    }
  }
  // The actual disconnect — runs only after the user confirms in the Casevo
  // dialogue (opened by the Disconnect button).
  const confirmDisconnect = async (): Promise<void> => {
    setConfirmOpen(false)
    setBusy(true)
    setError(null)
    try {
      await msDisconnectApi()
      await load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not disconnect.')
    } finally {
      setBusy(false)
    }
  }

  const connected = !!status?.connected
  // Stored, but its tokens can't be read any more (e.g. a server key change):
  // shown as needing a reconnect, not "Connected" — Reconnect replaces it.
  const needsReconnect = !connected && !!status?.needsReconnect
  return (
    <SettingsCard
      icon="ms"
      title="Microsoft account"
      sub="Send email from Casevo and see your Outlook calendar in the diary."
      right={loading ? null : (
        <SettingsBadge on={connected} dot={connected}>{connected ? 'Connected' : needsReconnect ? 'Reconnect needed' : 'Not connected'}</SettingsBadge>
      )}
    >
      {banner && <ActionBox live variant="slim" tone={banner.ok ? 'done' : 'danger'} title={banner.text} className="mt-[12px]" />}
      {error && <ErrorNote className="mt-[12px]">{error}</ErrorNote>}

      {loading ? (
        <SettingsToggleRow title={<span className="text-casevo-text-soft font-normal">Loading…</span>} />
      ) : connected ? (
        <SettingsToggleRow
          title={<>Connected as {status?.msEmail || 'your Microsoft account'}</>}
          desc="Casevo can send mail as you and read your calendar. Disconnect any time."
          control={
            <button type="button" onClick={() => setConfirmOpen(true)} disabled={busy} className={S_BTN_GHOST_SM}>
              Disconnect
            </button>
          }
        />
      ) : needsReconnect ? (
        <SettingsToggleRow
          title={<>Reconnect {status?.msEmail || 'your Microsoft account'}</>}
          desc="Casevo can no longer use this connection. Reconnect to keep sending email and seeing your Outlook calendar."
          control={
            <button type="button" onClick={() => { void handleConnect() }} disabled={busy} className={S_BTN_PRIMARY_SM}>
              {busy ? 'Redirecting…' : 'Reconnect'}
            </button>
          }
        />
      ) : (
        <SettingsToggleRow
          title="Not connected"
          desc="Connect your Microsoft account to send email from your own mailbox and see your Outlook calendar in the diary."
          control={
            <button type="button" onClick={() => { void handleConnect() }} disabled={busy} className={S_BTN_PRIMARY_SM}>
              {busy ? 'Redirecting…' : 'Connect Microsoft'}
            </button>
          }
        />
      )}

      {/* Casevo confirm dialogue for Disconnect (replaces the old native
          window.confirm). Disconnect uses the built-in destructive styling. */}
      <ConfirmDialogue
        isOpen={confirmOpen}
        title="Disconnect Microsoft account?"
        message="You'll need to reconnect to use email and calendar sync."
        confirmLabel="Disconnect"
        onConfirm={() => { void confirmDisconnect() }}
        onCancel={() => setConfirmOpen(false)}
      />
    </SettingsCard>
  )
}

export default MicrosoftPanel
